import type { DiffEditorSnapshot } from "../../ui/diffTabMonaco.js";
import type { LanguageMode } from "../../doc/languageMode.js";
import { createDirectoryHandleFs } from "./directoryHandleFs.js";
import { formatGitWorkspaceError } from "./gitPackfileHints.js";
import { logWithWorkerFallback } from "./isoGitWorkerClient.js";
import { getLargestPackfileBytes } from "./workspacePackStats.js";
import { GIT_DEFAULT_REMOTE, GIT_GITDIR, GIT_WORKDIR } from "./gitConstants.js";
import {
  gitWorkspaceCheckoutLocal,
  gitWorkspaceCheckoutRemote,
  gitWorkspaceCommit,
  gitWorkspaceCreateBranch,
  gitWorkspaceCreateTag,
  gitWorkspaceDeleteBranch,
  gitWorkspaceFetch,
  gitWorkspaceListLocalBranches,
  gitWorkspaceListRemoteBranches,
  gitWorkspacePull,
  gitWorkspacePush,
  gitWorkspaceRenameBranch,
  gitWorkspaceStageAll,
  gitWorkspaceStashApply,
  gitWorkspaceStashListText,
  gitWorkspaceStashPop,
  gitWorkspaceStashPush,
  gitWorkspaceStatusText,
  gitWorkspaceUnstageAll,
  type GitNetworkProgress,
} from "./gitWorkspaceOperations.js";
import { runGitWorkspaceDiffCompare } from "./gitWorkspaceDiff.js";
import { runGitWorkspaceShow } from "./gitWorkspaceShow.js";

const STATUS_TIMEOUT_MS = 45_000;

export type GitTerminalRunResult = {
  stdout: string;
  stderr?: string;
  exitCode: number;
  /** When set, host should refresh the folder tree. */
  workspaceMutated?: boolean;
  /**
   * When set, the terminal panel shows a confirmation widget and then calls
   * `onOpenGitDiffBatch` with the selected snapshots.
   * A single-file explicit diff will have exactly one entry (opens without confirmation).
   */
  openDiffSnapshots?: DiffEditorSnapshot[];
  /** Total number of changed paths before truncation (for the confirmation copy). */
  openDiffSnapshotsTotalCount?: number;
  /** Non-empty when the list was truncated to GIT_DIFF_MAX_SNAPSHOTS. */
  openDiffSnapshotsTruncatedNote?: string;
  /**
   * When set, the panel opens a new editable text tab pre-populated with `content`.
   * Used by `git show <ref>:<path>`.
   */
  openTextTab?: { title: string; content: string; language: LanguageMode };
};

/** Minimal shell-like tokenizer (double quotes only). */
export function parseShellWords(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (c === '"' && inQuote) {
      inQuote = false;
      out.push(cur);
      cur = "";
      continue;
    }
    if (c === '"' && !inQuote) {
      inQuote = true;
      continue;
    }
    if (!inQuote && /\s/.test(c)) {
      if (cur.length) {
        out.push(cur);
        cur = "";
      }
      continue;
    }
    cur += c;
  }
  if (cur.length || inQuote) out.push(cur);
  return out.filter((s) => s.length > 0);
}

export function parseLogArgs(argv: string[]): {
  depth: number;
  ref?: string;
  prettyFormat?: string;
  dateFormat?: string;
} {
  let depth = 20;
  let ref: string | undefined;
  let prettyFormat: string | undefined;
  let dateFormat: string | undefined;
  for (let i = 0; i < argv.length; i++) {
    const a = argv[i]!;
    if (a === "-n" || a === "--max-count") {
      depth = Math.max(1, parseInt(argv[++i] ?? "20", 10) || 20);
    } else if (a.startsWith("--max-count=")) {
      depth = Math.max(1, parseInt(a.slice("--max-count=".length), 10) || 20);
    } else if (a === "--pretty") {
      const v = argv[++i] ?? "";
      if (v.startsWith("format:")) prettyFormat = v.slice("format:".length);
    } else if (a.startsWith("--pretty=format:")) {
      prettyFormat = a.slice("--pretty=format:".length);
    } else if (a === "--date") {
      const v = argv[++i] ?? "";
      if (v.startsWith("format:")) dateFormat = v.slice("format:".length);
    } else if (a.startsWith("--date=format:")) {
      dateFormat = a.slice("--date=format:".length);
    } else if (a === "--oneline" || a === "--decorate" || a === "--graph") {
      continue;
    } else if (!a.startsWith("-")) {
      ref = a;
    }
  }
  return { depth, ref, prettyFormat, dateFormat };
}

function formatLogDate(timestamp: number, timezoneOffset: number, pattern: string): string {
  const d = new Date((timestamp - timezoneOffset * 60) * 1000);
  const pad2 = (n: number): string => String(n).padStart(2, "0");
  return pattern
    .replace(/%Y/g, String(d.getUTCFullYear()))
    .replace(/%m/g, pad2(d.getUTCMonth() + 1))
    .replace(/%d/g, pad2(d.getUTCDate()))
    .replace(/%H/g, pad2(d.getUTCHours()))
    .replace(/%M/g, pad2(d.getUTCMinutes()))
    .replace(/%S/g, pad2(d.getUTCSeconds()));
}

/** Expands a small git-log `--pretty=format:` subset (`%h`, `%H`, `%s`, `%ad`, `%%`). */
export function formatCommitPrettyLine(
  commit: { oid: string; commit: { message: string; author: { timestamp: number; timezoneOffset: number } } },
  prettyFormat: string,
  dateFormat?: string,
): string {
  const subject = commit.commit.message.trim().split("\n")[0] ?? "";
  const ad = (): string =>
    dateFormat
      ? formatLogDate(commit.commit.author.timestamp, commit.commit.author.timezoneOffset, dateFormat)
      : new Date(commit.commit.author.timestamp * 1000).toISOString();

  let out = "";
  let i = 0;
  while (i < prettyFormat.length) {
    const c = prettyFormat[i]!;
    if (c !== "%") {
      out += c;
      i++;
      continue;
    }
    const rest = prettyFormat.slice(i);
    if (rest.startsWith("%%")) {
      out += "%";
      i += 2;
      continue;
    }
    // Longest non-ambiguous tokens first (`%ad` must not be parsed as `%a` + `d`).
    if (rest.startsWith("%ad")) {
      out += ad();
      i += 3;
      continue;
    }
    if (rest.startsWith("%h")) {
      out += commit.oid.slice(0, 7);
      i += 2;
      continue;
    }
    if (rest.startsWith("%H")) {
      out += commit.oid;
      i += 2;
      continue;
    }
    if (rest.startsWith("%s")) {
      out += subject;
      i += 2;
      continue;
    }
    // Unknown `%` sequence: keep literal (same length as unknown token would be confusing; emit `%` only).
    out += "%";
    i++;
  }
  return out;
}

async function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race([
      p,
      new Promise<T>((_, reject) => {
        timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s.`)), ms);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function formatBranchList(root: FileSystemDirectoryHandle, includeRemote: boolean): Promise<string> {
  const fs = createDirectoryHandleFs(root);
  const { listBranches, currentBranch } = await import("isomorphic-git");
  const cur = await currentBranch({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, fullname: false });
  const locals = await listBranches({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR });
  const lines: string[] = [];
  for (const b of locals) {
    const mark = b === cur ? "* " : "  ";
    lines.push(`${mark}${b}`);
  }
  if (includeRemote) {
    const remotes = await gitWorkspaceListRemoteBranches(root);
    for (const b of remotes) {
      lines.push(`  remotes/${GIT_DEFAULT_REMOTE}/${b}`);
    }
  }
  return lines.join("\n");
}

async function formatTagList(root: FileSystemDirectoryHandle): Promise<string> {
  const fs = createDirectoryHandleFs(root);
  const { listTags } = await import("isomorphic-git");
  const tags = await listTags({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR });
  if (!Array.isArray(tags) || tags.length === 0) return "";
  return [...tags].sort().join("\n");
}

/**
 * Runs a single-line `git …` command against the folder workspace root (isomorphic-git; no system git binary).
 */
export async function runGitTerminalLine(
  root: FileSystemDirectoryHandle,
  line: string,
  opts?: { onProgress?: (p: GitNetworkProgress) => void },
): Promise<GitTerminalRunResult> {
  const trimmed = line.trim();
  if (!trimmed || trimmed.startsWith("#")) {
    return { stdout: "", exitCode: 0 };
  }

  const argv = parseShellWords(trimmed);
  if (argv.length === 0) return { stdout: "", exitCode: 0 };

  const cmd0 = argv[0]!.toLowerCase();
  if (cmd0 !== "git") {
    return {
      stdout: "",
      stderr: 'Type git commands only (example: git status). Use "clear" or "cls" to wipe the view.',
      exitCode: 1,
    };
  }

  const sub = (argv[1] ?? "").toLowerCase();
  const rest = argv.slice(2);

  try {
    switch (sub) {
      case "":
      case "--help":
      case "-h":
      case "help": {
        const help = [
          "Git terminal (browser / isomorphic-git). Close with × in the header; reopen via View → Git terminal.",
          "Examples:",
          "  git status          git branch [-a]     git log [-n 20] [ref]",
          "    status legend (XY): X=index, Y=worktree. ' M' unstaged modify, 'M ' staged modify, ' D' unstaged delete, 'D ' staged delete, 'A ' staged add, '??' untracked (with -u).",
          "  git clone <url> [directory]   (prompts for destination folder and opens it as workspace)",
          "  git fetch           git pull            git push [--force]",
          "  git checkout <ref>  git switch <ref>    git checkout -b <name>",
          "  git branch <name>   git branch -d/-D <name>   git branch -m <old> <new>",
          "  git add [-A|.]      git reset           git commit -m \"msg\"",
          "  git diff [path]     git diff --cached [path]   git diff HEAD [path]  (opens compare tab)",
          "  git show [<ref>]    git show <ref>:<path>       (commit info / compare tabs / file at commit)",
          "  git stash [push]    git stash list      git stash pop | apply",
          "  git tag             git tag <name>      git version",
          "Push has no built-in credential UI; use a public remote or embed credentials in the remote URL if your host allows it.",
        ].join("\n");
        return { stdout: help, exitCode: 0 };
      }

      case "version":
      case "--version": {
        const { version } = await import("isomorphic-git");
        return { stdout: `git version (isomorphic-git ${version})`, exitCode: 0 };
      }

      case "status": {
        const includeUntracked =
          rest.includes("-u") ||
          rest.includes("--untracked-files") ||
          rest.includes("--untracked-files=all");
        const t = await withTimeout(
          gitWorkspaceStatusText(root, 200, { includeUntracked }),
          STATUS_TIMEOUT_MS,
          "git status",
        );
        return { stdout: t, exitCode: 0 };
      }

      case "branch": {
        const a0 = rest[0] ?? "";
        if (a0 === "-a" || a0 === "--all") {
          return { stdout: await formatBranchList(root, true), exitCode: 0 };
        }
        if (a0 === "-d" || a0 === "-D") {
          const name = rest[1];
          if (!name) return { stdout: "", stderr: "usage: git branch -d <branch>", exitCode: 1 };
          await gitWorkspaceDeleteBranch(root, name);
          return { stdout: "", exitCode: 0, workspaceMutated: true };
        }
        if (a0 === "-m" || a0 === "-M") {
          const oldb = rest[1];
          const newb = rest[2];
          if (oldb && newb) {
            await gitWorkspaceRenameBranch(root, oldb, newb);
            return { stdout: "", exitCode: 0, workspaceMutated: true };
          }
          if (oldb && !newb) {
            const fs = createDirectoryHandleFs(root);
            const { currentBranch } = await import("isomorphic-git");
            const cur = await currentBranch({ fs, dir: GIT_WORKDIR, gitdir: GIT_GITDIR, fullname: false });
            if (!cur) return { stdout: "", stderr: "detached HEAD: use git branch -m <old> <new>", exitCode: 1 };
            await gitWorkspaceRenameBranch(root, cur, oldb);
            return { stdout: "", exitCode: 0, workspaceMutated: true };
          }
          return { stdout: "", stderr: "usage: git branch -m <new>  or  git branch -m <old> <new>", exitCode: 1 };
        }
        if (rest.length === 1 && !rest[0]!.startsWith("-")) {
          await gitWorkspaceCreateBranch(root, rest[0]!, false);
          return { stdout: "", exitCode: 0, workspaceMutated: true };
        }
        if (rest.length === 0) {
          return { stdout: await formatBranchList(root, false), exitCode: 0 };
        }
        return { stdout: "", stderr: `unsupported: git branch ${rest.join(" ")}`, exitCode: 1 };
      }

      case "log": {
        const { depth, ref, prettyFormat, dateFormat } = parseLogArgs(rest);
        const packBytes = await getLargestPackfileBytes(root);
        const commits = await logWithWorkerFallback(root, packBytes, depth, ref, async () => {
          const fs = createDirectoryHandleFs(root);
          const { log } = await import("isomorphic-git");
          return log({
            fs,
            dir: GIT_WORKDIR,
            gitdir: GIT_GITDIR,
            depth,
            ...(ref ? { ref } : {}),
          });
        });
        if (!commits.length) return { stdout: "(no commits)", exitCode: 0 };
        const text = commits
          .map((c) =>
            prettyFormat
              ? formatCommitPrettyLine(c, prettyFormat, dateFormat)
              : `${c.oid.slice(0, 7)} ${c.commit.message.trim().split("\n")[0] ?? ""}`,
          )
          .join("\n");
        return { stdout: text, exitCode: 0 };
      }

      case "fetch": {
        await gitWorkspaceFetch(root, opts?.onProgress);
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "pull": {
        await gitWorkspacePull(root, opts?.onProgress);
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "push": {
        const force = rest.includes("--force") || rest.includes("-f");
        await gitWorkspacePush(root, { force });
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "checkout":
      case "switch": {
        if (rest[0] === "-b" || rest[0] === "-c") {
          const name = rest[1];
          if (!name) return { stdout: "", stderr: `usage: git ${sub} ${rest[0]} <branch>`, exitCode: 1 };
          await gitWorkspaceCreateBranch(root, name, true);
          return { stdout: "", exitCode: 0, workspaceMutated: true };
        }
        const ref = rest.find((x) => !x.startsWith("-"));
        if (!ref) return { stdout: "", stderr: `usage: git ${sub} <branch>`, exitCode: 1 };
        const remotes = await gitWorkspaceListRemoteBranches(root);
        if (remotes.includes(ref)) {
          await gitWorkspaceCheckoutRemote(root, ref);
        } else {
          await gitWorkspaceCheckoutLocal(root, ref);
        }
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "add": {
        const ok = rest.length === 0 || rest.includes(".") || rest.includes("-A") || rest.includes("--all");
        if (!ok) return { stdout: "", stderr: "only git add . | -A | --all is supported", exitCode: 1 };
        await gitWorkspaceStageAll(root);
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "reset": {
        const hasUnsupportedResetFlags =
          rest.includes("--soft") || rest.includes("--hard") || rest.includes("--mixed");
        if (hasUnsupportedResetFlags) {
          return {
            stdout: "",
            stderr: "Only unstaging is supported: git reset (same as git reset HEAD).",
            exitCode: 1,
          };
        }
        await gitWorkspaceUnstageAll(root);
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "commit": {
        let msg = "";
        for (let i = 0; i < rest.length; i++) {
          if (rest[i] === "-m" || rest[i] === "--message") {
            msg = rest[i + 1] ?? "";
            break;
          }
        }
        const oid = await gitWorkspaceCommit(root, msg);
        return { stdout: `[${oid.slice(0, 7)}]`, exitCode: 0, workspaceMutated: true };
      }

      case "stash": {
        const op = (rest[0] ?? "push").toLowerCase();
        if (op === "list") {
          return { stdout: await gitWorkspaceStashListText(root), exitCode: 0 };
        }
        if (op === "pop") {
          await gitWorkspaceStashPop(root);
          return { stdout: "", exitCode: 0, workspaceMutated: true };
        }
        if (op === "apply") {
          await gitWorkspaceStashApply(root);
          return { stdout: "", exitCode: 0, workspaceMutated: true };
        }
        if (op === "push" || rest.length === 0) {
          let message = "";
          const mi = rest.indexOf("-m");
          if (mi >= 0) message = rest[mi + 1] ?? "";
          await gitWorkspaceStashPush(root, message);
          return { stdout: "", exitCode: 0, workspaceMutated: true };
        }
        return { stdout: "", stderr: `unsupported: git stash ${rest.join(" ")}`, exitCode: 1 };
      }

      case "tag": {
        if (rest.length === 0) {
          const t = await formatTagList(root);
          return { stdout: t || "(no tags)", exitCode: 0 };
        }
        const name = rest[0]!;
        const force = rest.includes("-f") || rest.includes("--force");
        await gitWorkspaceCreateTag(root, name, force);
        return { stdout: "", exitCode: 0, workspaceMutated: true };
      }

      case "diff": {
        return runGitWorkspaceDiffCompare(root, rest);
      }

      case "show": {
        return runGitWorkspaceShow(root, rest);
      }

      default:
        return {
          stdout: "",
          stderr: `unsupported git command: ${sub}. Try git help.`,
          exitCode: 1,
        };
    }
  } catch (e) {
    return { stdout: "", stderr: formatGitWorkspaceError(e), exitCode: 1 };
  }
}
